import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { forwardRef, memo, useMemo, type FC, type PropsWithChildren } from "react";

import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";

const BRAND_COLORS = [
    cn("bg-surface-brand-accent-01 text-content-brand-accent-02"),
    cn("bg-surface-brand-accent-02 text-content-brand-accent-02"),
    cn("bg-surface-brand-accent-03 text-content-brand-accent-02"),
    cn("bg-surface-brand-accent-04 text-content-brand-accent-03"),
    cn("bg-surface-brand-accent-05 text-content-brand"),
    cn("bg-surface-brand-accent-06 text-content-brand-accent-04"),
    cn("bg-surface-brand-accent-07 text-content-brand-accent-05"),
    cn("bg-surface-brand-accent-08 text-content-brand-accent-06"),
    cn("bg-surface-brand-accent-09 text-content-brand-accent-07"),
] as const;

// Optimized hash function with bitwise operations
const computeHash = (str: string): number => {
    let hash = 0;
    const len = str.length;
    for (let i = 0; i < len; i++) {
        hash = ((hash << 5) - hash + str.charCodeAt(i)) & 0xffffffff;
    }
    return Math.abs(hash);
};

const computeInitials = (name: string): string => {
    if (!name) {
        return "?";
    }
    const words = name
        .split(" ")
        .filter((w) => !!w)
        .map((n) => n.replace(/[^a-zA-Z]/g, ""))
        .filter((n) => n.length > 0);

    if (words.length === 0) {
        return "?";
    }

    if (words.length === 1) {
        return words[0].substring(0, 1).toUpperCase();
    }

    return words
        .map((n) => n[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
};

const SIZE_CONFIG = {
    16: { container: "size-4", font: cn("text-[10px] leading-4") },
    24: { container: "size-6", font: cn("text-xs leading-6") },
    32: { container: "size-8", font: cn("text-xs leading-8") },
    48: { container: "size-12", font: cn("text-sm leading-12") },
} as const;

export type AvatarSize = keyof typeof SIZE_CONFIG;

/**
 * @example
 * <Avatar size={48} className="border border-border-base">
 *     {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={`${user.name || "User"}'s avatar`} />}
 *     <AvatarFallback>
 *         <AvatarInitials name={user?.name || "User"} size={48} />
 *     </AvatarFallback>
 * </Avatar>
 */
const Avatar = forwardRef<
    React.ElementRef<typeof AvatarPrimitive.Root>,
    React.ComponentProps<typeof AvatarPrimitive.Root> & {
        size?: AvatarSize;
    }
>(function Avatar({ className, size = 32, ...props }, ref) {
    const config = SIZE_CONFIG[size];

    return (
        <AvatarPrimitive.Root
            ref={ref}
            data-slot="avatar"
            className={cn("relative flex shrink-0 overflow-hidden rounded-full", config.container, className)}
            {...props}
        />
    );
});

Avatar.displayName = "Avatar";

function AvatarImage({ className, loading = "lazy", ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
    return (
        <AvatarPrimitive.Image
            data-slot="avatar-image"
            data-testid="avatar-image"
            className={cn("aspect-square size-full object-cover", className)}
            referrerPolicy="no-referrer"
            loading={loading}
            {...props}
        />
    );
}

function AvatarFallback({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
    return (
        <AvatarPrimitive.Fallback
            data-slot="avatar-fallback"
            className={cn("flex size-full items-center justify-center rounded-full", className)}
            {...props}
        />
    );
}

type AvatarInitialsProps = PropsWithClassName & {
    /** Name for generating initials */
    name: string;
    /** Avatar size for font scaling */
    size?: AvatarSize;
};

const AvatarInitials: FC<AvatarInitialsProps> = memo(({ name, size = 32, className }) => {
    const initials = useMemo(() => computeInitials(name), [name]);

    const colorClassName = useMemo(() => {
        return BRAND_COLORS[computeHash(name) % BRAND_COLORS.length];
    }, [name]);

    const config = SIZE_CONFIG[size];

    return (
        <div
            className={cn(
                "inline-flex size-full select-none items-center justify-center font-medium",
                config.font,
                colorClassName,
                className,
            )}
            role="img"
            aria-label={`${name}'s avatar`}
        >
            <span className="inline-block text-center">{initials}</span>
        </div>
    );
});

AvatarInitials.displayName = "AvatarInitials";

type AvatarStaticIconProps = PropsWithClassName &
    PropsWithChildren & {
        /** Identifier is used for picking a background color to use */
        identifier: string;
        /** Avatar size for font scaling */
        size?: AvatarSize;
    };

const AvatarStaticIcon: FC<AvatarStaticIconProps> = memo(({ identifier: name, size = 32, children, className }) => {
    const colorClassName = useMemo(() => {
        return BRAND_COLORS[computeHash(name) % BRAND_COLORS.length];
    }, [name]);

    const config = SIZE_CONFIG[size];

    return (
        <div
            className={cn(
                "inline-flex size-full select-none items-center justify-center",
                config.font,
                colorClassName,
                className,
            )}
            role="img"
            aria-label={`${name}'s avatar`}
        >
            {children}
        </div>
    );
});

AvatarStaticIcon.displayName = "AvatarStaticIcon";

export { Avatar, AvatarFallback, AvatarImage, AvatarInitials, AvatarStaticIcon };
